import type {
  CustomerListFilters,
  LaboratoryResultListFilters,
  QuotationsListFilters,
  QuotationsListSort,
  WorkOrdersListFilters,
} from '../api/strapiCrm';
import { emptyLaboratoryResultListFilters } from '../api/strapiCrm';
import type { UsersListFilters } from '../api/strapiUsers';

/**
 * Central query keys for Strapi / server state (TanStack Query).
 * Keep keys consistent so invalidation stays predictable.
 */
export const queryKeys = {
  strapi: ['strapi'] as const,

  me(jwt: string | null) {
    return [...queryKeys.strapi, 'me', jwt] as const;
  },

  navigationMenu(jwt: string | null) {
    return [...queryKeys.strapi, 'navigationMenu', jwt] as const;
  },

  surveyRows(jwt: string | null, userId?: number | null) {
    return [...queryKeys.strapi, 'surveyRows', jwt, userId ?? 'all'] as const;
  },

  surveyRowsPrefix(jwt: string | null) {
    return [...queryKeys.strapi, 'surveyRows', jwt] as const;
  },

  surveyRowsForWorkOrder(jwt: string | null, workOrderDocumentId: string | null) {
    return [...queryKeys.strapi, 'surveyRows', jwt, 'workOrder', workOrderDocumentId] as const;
  },

  syncSamplingPlansForWorkOrder(jwt: string | null, workOrderDocumentId: string | null) {
    return [...queryKeys.strapi, 'syncSamplingPlans', jwt, workOrderDocumentId] as const;
  },

  preservationGridRows(jwt: string | null, sort?: { field: string; direction: string }) {
    return [
      ...queryKeys.strapi,
      'preservationGridRows',
      jwt,
      sort?.field ?? 'berita_acara_created_at',
      sort?.direction ?? 'desc',
    ] as const;
  },

  preservationGridRowsPrefix(jwt: string | null) {
    return [...queryKeys.strapi, 'preservationGridRows', jwt] as const;
  },

  laboratoryResultGridRows(
    jwt: string | null,
    filters: LaboratoryResultListFilters = emptyLaboratoryResultListFilters,
  ) {
    return [...queryKeys.strapi, 'laboratoryResultGridRows', jwt, filters] as const;
  },

  laboratoryResultGridRowsPrefix(jwt: string | null) {
    return [...queryKeys.strapi, 'laboratoryResultGridRows', jwt] as const;
  },

  laboratoryResultForm(jwt: string | null, beritaAcaraDocumentId: string | null) {
    return [...queryKeys.strapi, 'laboratoryResultForm', jwt, beritaAcaraDocumentId] as const;
  },

  laboratoryResultWorkOrderForm(jwt: string | null, workOrderDocumentId: string | null) {
    return [...queryKeys.strapi, 'laboratoryResultWorkOrderForm', jwt, workOrderDocumentId] as const;
  },

  invoiceGridRows(jwt: string | null, sort?: string) {
    return sort
      ? ([...queryKeys.strapi, 'invoiceGridRows', jwt, sort] as const)
      : ([...queryKeys.strapi, 'invoiceGridRows', jwt] as const);
  },

  invoicePayments(jwt: string | null, workOrderDocumentId: string | null) {
    return [...queryKeys.strapi, 'invoicePayments', jwt, workOrderDocumentId] as const;
  },

  lhuCustomHtml(jwt: string | null, workOrderDocumentId: string | null) {
    return [...queryKeys.strapi, 'lhuCustomHtml', jwt, workOrderDocumentId] as const;
  },

  secondaryDataTemplate(jwt: string | null, beritaAcaraDocumentId: string | null) {
    return [...queryKeys.strapi, 'secondaryDataTemplate', jwt, beritaAcaraDocumentId] as const;
  },

  secondaryDataBiologyTemplates(jwt: string | null) {
    return [...queryKeys.strapi, 'secondaryDataBiologyTemplates', jwt] as const;
  },

  lhuPdfHtmlPreview(jwt: string | null, workOrderDocumentId: string | null) {
    return [...queryKeys.strapi, 'lhuPdfHtmlPreview', jwt, workOrderDocumentId] as const;
  },

  /** Prefix: invalidates every `customers(jwt, …)` list query. */
  customersListPrefix(jwt: string | null) {
    return [...queryKeys.strapi, 'customers', jwt] as const;
  },

  customers(jwt: string | null, filters: CustomerListFilters) {
    return [
      ...queryKeys.customersListPrefix(jwt),
      filters.customerNumber,
      filters.name,
      filters.email,
      filters.phone,
      filters.status,
    ] as const;
  },

  /** Paged customer list: same filters as `customers`, plus Strapi page and pageSize. `picScope` kosong = tanpa cek quotation. */
  customersPage(
    jwt: string | null,
    filters: CustomerListFilters,
    page: number,
    pageSize: number,
    picScope: string | null = null,
  ) {
    return [...queryKeys.customers(jwt, filters), page, pageSize, picScope] as const;
  },

  customersSearch(jwt: string | null, qTrimmed: string) {
    return [...queryKeys.strapi, 'customersSearch', jwt, qTrimmed] as const;
  },

  customerDetail(jwt: string | null, documentId: string | null) {
    return [...queryKeys.strapi, 'customerDetail', jwt, documentId] as const;
  },

  workOrdersListPrefix(jwt: string | null) {
    return [...queryKeys.strapi, 'workOrders', jwt] as const;
  },

  workOrders(jwt: string | null, filters: WorkOrdersListFilters) {
    return [
      ...queryKeys.workOrdersListPrefix(jwt),
      filters.quotationRefs,
      filters.customer,
      filters.workOrderDate,
      filters.status,
      filters.workOrderDocumentId,
      filters.surveyTeamUserId ?? null,
    ] as const;
  },

  workOrderDetail(jwt: string | null, documentId: string | null) {
    return [...queryKeys.strapi, 'workOrderDetail', jwt, documentId] as const;
  },

  samplingCheckTools(jwt: string | null, workOrderDocumentId: string | null) {
    return [...queryKeys.strapi, 'samplingCheckTools', jwt, workOrderDocumentId] as const;
  },

  sampleInformationsFlat(jwt: string | null) {
    return [...queryKeys.strapi, 'sampleInformationsFlat', jwt] as const;
  },

  testResultsFlat(jwt: string | null) {
    return [...queryKeys.strapi, 'testResultsFlat', jwt] as const;
  },

  quotationsListPrefix(jwt: string | null) {
    return [...queryKeys.strapi, 'quotations', jwt] as const;
  },

  quotations(jwt: string | null, filters: QuotationsListFilters) {
    return [
      ...queryKeys.quotationsListPrefix(jwt),
      filters.quotationNumber,
      filters.customer,
      filters.assigneeContains,
      filters.approverContains,
      filters.status,
      filters.contract,
      filters.consultantQuote,
    ] as const;
  },

  /** Paged quotation list: same filters as `quotations`, plus sort, page, and pageSize. */
  quotationsPage(
    jwt: string | null,
    filters: QuotationsListFilters,
    sort: QuotationsListSort,
    page: number,
    pageSize: number,
    visibility?: string,
    picAssigneeKey?: string,
  ) {
    return [
      ...queryKeys.quotations(jwt, filters),
      sort.field,
      sort.direction,
      page,
      pageSize,
      visibility ?? 'all',
      picAssigneeKey ?? '',
    ] as const;
  },

  quotationDetail(jwt: string | null, documentId: string | null) {
    return [...queryKeys.strapi, 'quotationDetail', jwt, documentId] as const;
  },

  companyBranches(jwt: string | null) {
    return [...queryKeys.strapi, 'companyBranches', jwt] as const;
  },

  quotationLogs(jwt: string | null, quotationDocumentId: string | null) {
    return [...queryKeys.strapi, 'quotationLogs', jwt, quotationDocumentId] as const;
  },

  users(jwt: string | null, filters: Pick<UsersListFilters, 'emailContains' | 'searchContains' | 'status' | 'userTypeCode'>) {
    return [
      ...queryKeys.strapi,
      'users',
      jwt,
      filters.emailContains,
      filters.searchContains ?? '',
      filters.status,
      filters.userTypeCode ?? '',
    ] as const;
  },

  userDetail(jwt: string | null, id: string | null) {
    return [...queryKeys.strapi, 'userDetail', jwt, id] as const;
  },

  permissionAccessCatalog(jwt: string | null) {
    return [...queryKeys.strapi, 'permissionAccessCatalog', jwt] as const;
  },

  permissionMaster(jwt: string | null) {
    return [...queryKeys.strapi, 'permissionMaster', jwt] as const;
  },

  userTypes(jwt: string | null) {
    return [...queryKeys.strapi, 'userTypes', jwt] as const;
  },

  customerSampleLocations(jwt: string | null, customerDocumentId: string | null) {
    return [...queryKeys.strapi, 'customerSampleLocations', jwt, customerDocumentId] as const;
  },

  masterEntityList(jwt: string | null, slug: string | null) {
    return [...queryKeys.strapi, 'masterEntityList', jwt, slug] as const;
  },

  masterEntityListPrefix(jwt: string | null, slug: string | null) {
    return queryKeys.masterEntityList(jwt, slug);
  },

  masterEntityListPage(jwt: string | null, slug: string | null, page: number, pageSize: number, filtersKey: string) {
    return [...queryKeys.masterEntityListPrefix(jwt, slug), page, pageSize, filtersKey] as const;
  },

  masterEntityMini(jwt: string | null, apiPlural: string) {
    return [...queryKeys.strapi, 'masterEntityMini', jwt, apiPlural] as const;
  },

  bakuMutusSearch(jwt: string | null, qTrimmed: string) {
    return [...queryKeys.strapi, 'bakuMutusSearch', jwt, qTrimmed] as const;
  },

  parameterUjiGridByBakuMutu(jwt: string | null, bakuMutuDocumentId: string | null) {
    return [...queryKeys.strapi, 'parameterUjiGridByBakuMutu', jwt, bakuMutuDocumentId] as const;
  },

  parameterUjisByDocumentIdsPack(jwt: string | null, sortedIdsKey: string) {
    return [...queryKeys.strapi, 'parameterUjisByDocumentIdsPack', jwt, sortedIdsKey] as const;
  },

  surveyTeamsSchedule(jwt: string | null, userNumericId: number | null) {
    return [...queryKeys.strapi, 'surveyTeamsSchedule', jwt, userNumericId] as const;
  },

  myUserSigned(jwt: string | null) {
    return [...queryKeys.strapi, 'myUserSigned', jwt] as const;
  },
} as const;
